// Types:
// circle, rect (simple objects :c)
//
//
//
//

export class BoundingBox {

  constructor(obj, options) {
    this.obj = obj;
    this.options = options || {};
  }

  has(x, y) {
    switch(this.obj.type) {
      case 'circle': {
        let radius = this.obj.drawOptions.radius || 0;
        let flag =
          (this.obj.x - (this.options.offsets ? this.options.offsets.x : 0) - radius < x && x < this.obj.x + radius)
          && (this.obj.y - (this.options.offsets ? this.options.offsets.y : 0) - radius < y && y < this.obj.y + radius);
        return flag;
      }
      case 'rect': {
        break;
      }
      default:
        const flag = (this.obj.x + (this.options.offsets ? this.options.offsets.x : 0) <= x && x <= this.obj.x + (this.options.offsets ? this.options.offsets.x : 0) + this.obj.width)
          && (this.obj.y + (this.options.offsets ? this.options.offsets.y : 0) <= y && y <= this.obj.y + (this.options.offsets ? this.options.offsets.y : 0) + this.obj.height);
        return flag;
    }
  }

  bounds() {
    switch(this.obj.type) {
      case 'circle':
        let radius = this.obj.drawOptions.radius || 0;

        return {
          x: this.obj.x,
          y: this.obj.y,
          width: 2 * radius,
          height: 2 * radius
        };

      default:
        return {
          x: this.obj.x,
          y: this.obj.y,
          width: this.obj.width,
          height: this.obj.height
        }
    }
  }

}

let id = 0;

export default class CanvasObject {

  constructor(parent, options, drawOptions, flags) {
    this.id = id++;
    this.x = options.x;
    this.y = options.y;
    this.width = options.width;
    this.height = options.height;
    this.draggable = options.draggable || false;
    this.type = options.type;
    this.drawOptions = drawOptions;
    this.boundingBox = new BoundingBox(this);
    this.constraints = options.constraints;
    this.eventHandlers = [];
    this.flags = flags;
    this.parent = parent;
    this.calulations();
  }

  getBoundingBox() {
    return this.boundingBox;
  }

  hasFlag(flag) {
    return this.flags && this.flags[flag];
  }

  getSize() {
    const bounds = this.boundingBox.bounds();
    return bounds.width * bounds.height;
  }

  on(evt, handler) {
    let handlers = this.eventHandlers[evt] || [];
    handlers.push(handler);
    this.eventHandlers[evt] = handlers;
  }

  setX(x) {
    this.callEvent('move', {from: {x: this.x, y: this.y}, to: {x, y: this.y}});
    this.x = x;
  }

  setY(y, eventName) {
    this.callEvent(eventName || 'move', {from: {x: this.x, y: this.y}, to: {x: this.x, y}});
    this.y = y;
  }

  callEvent(evt, ...args) {
    const eventObj = args[0] || null;
    const otherArgs = args.slice(1, args.length);
    if(this.eventHandlers[evt])
      this.eventHandlers[evt].forEach(handler => handler(eventObj, ...otherArgs));
    if(this.parent && this.parent.notifyUpdate) this.parent.notifyUpdate();
  }

  calulations() {
    if(this.constraints && this.constraints.doSnap) {

      let sf = this.constraints.snapFree !== undefined ? this.constraints.snapFree : true;
      let threshold_ratio;
      if(sf) {
        threshold_ratio = 1/10;
      } else {
        threshold_ratio = 1/2;
      }
      this.thresholdX = threshold_ratio * this.constraints.snap.colSpace;
      this.thresholdY = threshold_ratio * this.constraints.snap.rowSpace;

      if(!sf) {
        // Force this object to snap to nearest point on grid
        let trX = this.constraints.snap.colSpace;
        let trY = this.constraints.snap.rowSpace;

        let points = this.constraints.snap.gridPoints;
        let snappedPoint = points.find(p => {
          return p.x === this.x && p.y === this.y;
        })

        // If the point is already snapped, skip all the calculations
        if(snappedPoint) return;

        // Otherwise, let's found the nearest points
        let nearPoints = points.filter(p => {
          return (p.x - trX <= this.x && this.x <= p.x + trX
            && p.y - trY <= this.y && this.y <= p.y + trY);
        });

        // And filter them to actually find the nearest
        let point;
        let nearest;
        nearPoints.forEach(p => {
          let diffx = Math.abs(this.x - p.x);
          let diffy = Math.abs(this.y - p.y);
          let dist = Math.sqrt(Math.pow(diffx, 2) + Math.pow(diffy, 2));
          if(!nearest || dist < nearest) {
            nearest = dist;
            point = p;
          }
        });

        if(point) {
          this.x = point.x;
          this.y = point.y
        }
      }

    }
  }

  setState(newObj) {

    const drawOptions = newObj.drawOptions;

    this.updatePosition(newObj.x, newObj.y);
    this.draggable = newObj.draggable;
    this.type = newObj.type;
    this.drawOptions = drawOptions;
    this.boundingBox = newObj.boundingBox;
    this.constraints = newObj.constraints;
    this.calulations();

    return this;
  }

  updatePosition(x, y) {
    if(this.constraints) {
      if(x <= this.constraints.minX)
        x = this.constraints.minX;
      if(y <= this.constraints.minY)
        y = this.constraints.minY;
      if(x >= this.constraints.maxX)
        x = this.constraints.maxX;
      if(y >= this.constraints.maxY)
        y = this.constraints.maxY;

      let axis = this.constraints.axis || 'both';
      if(axis === 'y') x = this.x;
      if(axis === 'x') y = this.y;

      if(this.constraints.doSnap) {
        // Find next snap point based on x and y
        let sf = this.constraints.snapFree !== undefined ? this.constraints.snapFree : true;
        let points = this.constraints.snap.gridPoints;
        let point = points.find(p => {
          return p.x - this.thresholdX <= x && x <= p.x + this.thresholdX
            && p.y - this.thresholdY <= y && y <= p.y + this.thresholdY;
        })
        if(point) {
          this.callEvent('move', {from: {x: this.x, y: this.y}, to: {x: point.x, y: point.y}});
          this.x = point.x;
          this.y = point.y;
          return;
        } else {
          if(!sf)
            return;
        }
      }
    }
    this.callEvent('move', {from: {x: this.x, y: this.y}, to: {x,y}});
    this.x = x;
    this.y = y;
  }

  draw(context) {
    // console.log('REDRAW: ', this.x, this.y);
    context.save();
    context.beginPath();

    context.fillStyle = this.drawOptions.fill || 'white';
    let stroke = { style: 'white', width: 1};
    if(this.drawOptions.stroke)
      stroke = {
        style: this.drawOptions.stroke.split(' ')[0],
        width: parseFloat(this.drawOptions.stroke.split(' ')[1])
      }
    context.strokeStyle = stroke.style;
    context.lineWidth = stroke.width;

    context.globalCompositionOperation = 'destination-over';

    switch(this.type) {
      case 'circle':
        context.arc(this.x, this.y, this.drawOptions.radius || 10, Math.PI * 2, false);
        break;

      case 'rect':
        break;

      default:
        break;
    }

    context.closePath();
    context.fill();
    context.stroke();
    context.restore();


  }

}



// WEBPACK FOOTER //
// ./src/components/canvas/objects/CanvasObject.js